/**
 * Reconciliation disposition UI — the shared visual language for the budgeting
 * pipeline: a labelled chip, a foot-to-gross waterfall, and a grouped Bridge.
 *
 * All read the shared DISPOSITION_META so the rail, chip, waterfall and Bridge
 * never diverge. See budget_disposition_service (backend).
 */
import React from 'react';
import { cn } from '@/lib/utils';
import {
  BudgetDisposition,
  BudgetDispositionGroup,
  DISPOSITION_META,
  FinanceWorksheetPreview,
} from '@/services/api/organizationService';

const TONE_CLASS: Record<string, string> = {
  green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  blue: 'bg-sky-50 text-sky-700 border-sky-200',
  slate: 'bg-slate-100 text-slate-600 border-slate-200',
  red: 'bg-rose-50 text-rose-700 border-rose-200',
};

/** Solid fill per group for the waterfall + rail mini-bar (matches the tones). */
export const GROUP_FILL: Record<BudgetDispositionGroup, string> = {
  funded: '#10b981',     // emerald-500
  demand: '#0ea5e9',     // sky-500
  duplicate: '#f59e0b',  // amber-500
  adjustment: '#94a3b8', // slate-400
  quarantine: '#f43f5e', // rose-500
};

export const GROUP_LABEL: Record<BudgetDispositionGroup, string> = {
  funded: 'Funded',
  duplicate: 'Duplicates',
  demand: 'Demand',
  adjustment: 'Adjustments',
  quarantine: 'Quarantine',
};

/** Left-to-right order for the bridge/waterfall. */
export const GROUP_ORDER: BudgetDispositionGroup[] = ['funded', 'demand', 'quarantine', 'duplicate', 'adjustment'];

export interface DispositionCount { count: number; annual: number; }

export function DispositionChip({ disposition, label, tone, title, className }:
  { disposition?: BudgetDisposition | string | null; label?: string; tone?: keyof typeof TONE_CLASS; title?: string; className?: string }) {
  // Explicit override — used to split one disposition into clearer sub-states
  // in the drill-down (e.g. terminated_incumbent -> "Backfilled" vs "Leaver — no seat").
  if (label) {
    return (
      <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap', TONE_CLASS[tone ?? 'slate'], className)} title={title ?? label}>
        {label}
      </span>
    );
  }
  if (!disposition) return null;
  const meta = DISPOSITION_META[disposition as BudgetDisposition];
  if (!meta) return <span className={cn('text-xs text-muted-foreground', className)}>{disposition}</span>;
  return (
    <span
      className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap', TONE_CLASS[meta.tone], className)}
      title={meta.label}
    >
      {meta.label}
    </span>
  );
}

const rand = (n: number) => {
  const a = Math.abs(n);
  const s = a >= 1_000_000 ? `R${(a / 1_000_000).toFixed(1)}m` : a >= 1_000 ? `R${Math.round(a / 1000)}k` : `R${Math.round(a)}`;
  return n < 0 ? `−${s}` : s;
};

/** Sum a monthly_amounts map. */
function rowTotal(m?: Record<number, number> | null): number {
  if (!m) return 0;
  return Object.values(m).reduce((s, v) => s + (Number(v) || 0), 0);
}

/** Legacy pre-disposition rows: infer the adjustment dispositions from match_status. */
const LEGACY_STATUS_DISPOSITION: Record<string, string> = {
  savings: 'adj_saving', remove: 'adj_removal', special: 'adj_special',
};

/** Build disposition_counts from a list of staged/preview items (Reconcile/Review). */
export function countsFromItems(items: Array<{ disposition?: string | null; match_status?: string | null; monthly_amounts?: Record<number, number> }>): Record<string, DispositionCount> {
  const out: Record<string, DispositionCount> = {};
  for (const it of items) {
    const d = (it.disposition || LEGACY_STATUS_DISPOSITION[it.match_status || ''] || 'true_gap') as string;
    const rec = out[d] || (out[d] = { count: 0, annual: 0 });
    rec.count += 1;
    rec.annual += rowTotal(it.monthly_amounts);
  }
  return out;
}

/** Roll disposition_counts up into the five groups. */
export function groupCounts(counts: Record<string, DispositionCount>) {
  const g: Record<BudgetDispositionGroup, DispositionCount> = {
    funded: { count: 0, annual: 0 }, demand: { count: 0, annual: 0 }, duplicate: { count: 0, annual: 0 },
    adjustment: { count: 0, annual: 0 }, quarantine: { count: 0, annual: 0 },
  };
  let grossCount = 0, grossAnnual = 0;
  for (const [code, v] of Object.entries(counts)) {
    grossCount += v.count; grossAnnual += v.annual;
    const grp = DISPOSITION_META[code as BudgetDisposition]?.group ?? 'quarantine';
    g[grp].count += v.count; g[grp].annual += v.annual;
  }
  return { g, grossCount, grossAnnual };
}

const GROUP_KEYS: BudgetDispositionGroup[] = ['funded', 'demand', 'duplicate', 'adjustment', 'quarantine'];

/** True if a line's disposition matches a filter (a group name OR a disposition code). */
export function matchesDispositionFilter(disposition: string | null | undefined, filter: string | null): boolean {
  if (!filter) return true;
  const d = (disposition || 'true_gap') as string;
  if ((GROUP_KEYS as string[]).includes(filter)) {
    return (DISPOSITION_META[d as BudgetDisposition]?.group ?? 'quarantine') === filter;
  }
  return d === filter;
}

/** Human label for an active disposition filter (group or code). */
export function dispositionFilterLabel(filter: string | null): string {
  if (!filter) return '';
  if ((GROUP_KEYS as string[]).includes(filter)) return GROUP_LABEL[filter as BudgetDispositionGroup];
  return DISPOSITION_META[filter as BudgetDisposition]?.label ?? filter;
}

/**
 * Foot-to-gross waterfall — a stacked bar of the five disposition groups with a
 * legend and a "foots to gross" badge. The signature element of Reconcile + Review.
 */
export function ReconciliationWaterfall({ counts, title = 'Reconciliation bridge', compact = false, onFilter, activeFilter, linesTitle }:
  { counts: Record<string, DispositionCount>; title?: string; compact?: boolean; onFilter?: (key: string | null) => void; activeFilter?: string | null; linesTitle?: string }) {
  if (!counts || Object.keys(counts).length === 0) return null;
  const { g, grossCount, grossAnnual } = groupCounts(counts);
  // The stacked bar shows the POSITIVE groups only — signed adjustments
  // (savings/removals) cannot render as a segment; they stay in the legend
  // and in the gross figure so the header foots to the xlsx export.
  const sumAbs = GROUP_ORDER.filter((k) => k !== 'adjustment').reduce((s, k) => s + Math.abs(g[k].annual), 0) || 1;
  const funded = g.funded.annual;
  const clickable = !!onFilter;
  const toggle = (k: string) => onFilter?.(activeFilter === k ? null : k);

  return (
    <div className={cn('rounded-lg border bg-card', compact ? 'p-3' : 'p-4')}>
      <div className="flex items-baseline justify-between mb-2">
        <h4 className="text-sm font-semibold">{title}</h4>
        <span className="text-xs text-muted-foreground tabular-nums" title={linesTitle}>
          gross <span className="font-medium text-foreground">{rand(grossAnnual)}</span> · {grossCount.toLocaleString()} RF1 lines
          {g.adjustment.count > 0 && <> (incl. {rand(g.adjustment.annual)} adjustments)</>}
        </span>
      </div>
      <div className="flex h-6 overflow-hidden rounded-md" role="img"
        aria-label={`Bridge: funded ${rand(g.funded.annual)}, demand ${rand(g.demand.annual)}, quarantine ${rand(g.quarantine.annual)}, adjustments ${rand(g.adjustment.annual)}`}>
        {GROUP_ORDER.map((k) => {
          if (k === 'adjustment') return null;
          const w = (Math.abs(g[k].annual) / sumAbs) * 100;
          if (w < 0.4) return null;
          const seg = <span key={k} className={cn('h-full block', clickable && 'cursor-pointer', activeFilter && activeFilter !== k && 'opacity-40')}
            style={{ width: '100%', background: GROUP_FILL[k] }} title={`${GROUP_LABEL[k]} ${rand(g[k].annual)}`} />;
          return clickable
            ? <button key={k} style={{ width: `${w}%` }} onClick={() => toggle(k)} aria-label={`Filter ${GROUP_LABEL[k]}`}>{seg}</button>
            : <span key={k} style={{ width: `${w}%`, background: GROUP_FILL[k] }} title={`${GROUP_LABEL[k]} ${rand(g[k].annual)}`} />;
        })}
      </div>
      <div className="mt-2.5 flex flex-wrap gap-x-3.5 gap-y-1.5">
        {GROUP_ORDER.map((k) => g[k].count === 0 ? null : (
          clickable ? (
            <button key={k} onClick={() => toggle(k)}
              className={cn('inline-flex items-center gap-1.5 rounded-full px-1.5 py-0.5 text-xs transition-colors',
                activeFilter === k ? 'bg-heritage-blue/10 ring-1 ring-heritage-blue' : 'hover:bg-muted')}>
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: GROUP_FILL[k] }} />
              <span className="text-muted-foreground">{GROUP_LABEL[k]}</span>
              <span className="font-medium tabular-nums">{g[k].count.toLocaleString()} · {rand(g[k].annual)}</span>
            </button>
          ) : (
            <span key={k} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: GROUP_FILL[k] }} />
              {GROUP_LABEL[k]} <span className="font-medium text-foreground tabular-nums">{g[k].count.toLocaleString()} · {rand(g[k].annual)}</span>
            </span>
          )
        ))}
      </div>
      {!compact && (
        <div className="mt-3 flex items-center justify-between border-t pt-2">
          <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Net funded
          </span>
          <span className="text-sm font-semibold tabular-nums text-emerald-700">{rand(funded)}</span>
        </div>
      )}
    </div>
  );
}

/** Wizard-stage Bridge: the waterfall + per-group disposition chip breakdown.
 *  When `onFilter` is supplied the group headers and individual chips become
 *  clickable filters (drill into exactly those lines). `activeFilter` is the
 *  current group name or disposition code; clicking the active one clears it. */
export function ReconciliationBridgePanel({ preview, onFilter, activeFilter }:
  { preview: FinanceWorksheetPreview; onFilter?: (key: string | null) => void; activeFilter?: string | null }) {
  const counts = preview.disposition_counts;
  if (!counts || Object.keys(counts).length === 0) return null;
  const byGroup: Record<BudgetDispositionGroup, { code: string; count: number; annual: number }[]> = {
    funded: [], demand: [], duplicate: [], adjustment: [], quarantine: [],
  };
  for (const [code, v] of Object.entries(counts)) {
    const grp = DISPOSITION_META[code as BudgetDisposition]?.group ?? 'quarantine';
    byGroup[grp].push({ code, count: v.count, annual: v.annual });
  }
  const clickable = !!onFilter;
  const toggle = (k: string) => onFilter?.(activeFilter === k ? null : k);
  return (
    <div className="space-y-3">
      <ReconciliationWaterfall counts={counts} onFilter={onFilter} activeFilter={activeFilter}
        linesTitle="All RF1 worksheet lines in this upload, incl. savings/removal adjustments." />
      <div className="rounded-lg border bg-card p-4 space-y-3">
        {GROUP_ORDER.map((grp) => {
          const rows = byGroup[grp];
          if (!rows.length) return null;
          return (
            <div key={grp}>
              {clickable ? (
                <button onClick={() => toggle(grp)}
                  className={cn('mb-1 inline-flex items-center gap-1 rounded px-1 -ml-1 text-xs font-medium transition-colors',
                    activeFilter === grp ? 'text-heritage-blue' : 'text-muted-foreground hover:text-foreground')}>
                  {GROUP_LABEL[grp]}
                  <span className="opacity-60">{activeFilter === grp ? '×' : '›'}</span>
                </button>
              ) : (
                <div className="text-xs font-medium text-muted-foreground mb-1">{GROUP_LABEL[grp]}</div>
              )}
              <div className="flex flex-wrap gap-1.5">
                {rows.sort((a, b) => Math.abs(b.annual) - Math.abs(a.annual)).map((r) => (
                  clickable ? (
                    <button key={r.code} onClick={() => toggle(r.code)}
                      className={cn('inline-flex items-center gap-1.5 rounded-full px-1 py-0.5 text-xs transition-colors',
                        activeFilter === r.code ? 'bg-heritage-blue/10 ring-1 ring-heritage-blue' : 'hover:bg-muted')}>
                      <DispositionChip disposition={r.code} />
                      <span className="text-muted-foreground tabular-nums">{r.count} · {rand(r.annual)}</span>
                    </button>
                  ) : (
                    <span key={r.code} className="inline-flex items-center gap-1.5 text-xs">
                      <DispositionChip disposition={r.code} />
                      <span className="text-muted-foreground tabular-nums">{r.count} · {rand(r.annual)}</span>
                    </span>
                  )
                ))}
              </div>
            </div>
          );
        })}
        {(preview.role_move_pairs?.length || preview.duplicate_groups?.length) ? (
          <p className="text-xs text-muted-foreground border-t pt-2">
            {preview.duplicate_groups?.length ?? 0} worker(s) with multiple budget lines
            {preview.role_move_pairs?.length ? `, incl. ${preview.role_move_pairs.length} role move(s)` : ''} — the line matching the
            worker&apos;s current job title funds their seat; the other line is a budgeted vacancy.
          </p>
        ) : null}
      </div>
    </div>
  );
}
